import { useEffect, useRef } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'

import { db } from '../../config/firebase.config'
import { getCheckinDeviceId } from '_utils/checkin-device.util'

/**
 * OC-448: subscribe to a single "latest check-in" Firestore doc under
 * `checkinEvents/{corporateId}/{subcollection}/{docId}`.
 *
 * O(1) single-doc listen: no collection query, no composite index, no unbounded growth. Fires
 * `onEvent` per overwrite; the first snapshot (the pre-existing latest, possibly stale) is skipped
 * so only live check-ins after mount trigger. The event carries Spanner IDs only — the caller
 * resolves full member detail from Spanner via `checkInId`.
 */
const useLatestCheckInDoc = ({ corporateId, subcollection, docId }, onEvent) => {
  const onEventRef = useRef(onEvent)
  useEffect(() => {
    onEventRef.current = onEvent
  }, [onEvent])

  useEffect(() => {
    if (!corporateId || !docId) {
      return undefined
    }

    const eventDoc = doc(db, 'checkinEvents', corporateId, subcollection, docId)

    // OC-448: surface exactly which doc this window is bound to. A silent id mismatch between the
    // scanner window and this staff window is the #1 reason the widget never pops.
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.info(
        '[check-in widget] listening on checkinEvents/%s/%s/%s',
        corporateId,
        subcollection,
        docId
      )
    }

    // Skip the initial snapshot (the pre-existing latest); only react to live overwrites after mount.
    let primed = false

    const unsubscribe = onSnapshot(
      eventDoc,
      (snapshot) => {
        if (!primed) {
          primed = true
          return
        }
        if (!snapshot.exists()) {
          return
        }
        const data = snapshot.data()
        if (!data?.checkInId) {
          return
        }
        onEventRef.current?.({
          checkInId: data.checkInId,
          clientId: data.clientId,
          siteId: data.siteId,
          status: data.status,
          source: data.source,
          failReason: data.failReason ?? null,
        })
      },
      // Realtime is best-effort — a listener error (permission-denied from a tenant/rules mismatch,
      // or a dropped connection) must never break the widget: the list still loads and refetches from
      // Spanner. But NEVER swallow it silently — that hid a real permission-denied for hours. Log it so
      // the cause (esp. `permission-denied`) is visible in the console.
      (error) => {
        // eslint-disable-next-line no-console
        console.error(
          '[check-in widget] realtime listener error on checkinEvents/%s/%s/%s (%s): %s',
          corporateId,
          subcollection,
          docId,
          error?.code ?? 'unknown',
          error?.message ?? String(error)
        )
      }
    )

    return () => unsubscribe()
  }, [corporateId, subcollection, docId])
}

/**
 * THIS device's targeted signal (`devices/{deviceId}`, deviceId from localStorage): barcode/Agent
 * scans routed to the operating front-desk device only — used to pin/override the Most Recent card.
 * Every other staff member at the site sees nothing on this doc.
 */
export const useCheckInWidgetEvents = ({ corporateId }, onEvent) =>
  useLatestCheckInDoc(
    { corporateId, subcollection: 'devices', docId: getCheckinDeviceId() },
    onEvent
  )

/**
 * The site's ALL-SOURCES feed (`sites/{siteId}`): every check-in at the site — QR_CODE,
 * CONTACTLESS, IN_APP, FOB_SCAN, KIOSK, staff manual, barcode, Agent — for the unread badge and
 * live in-modal list. Replaces the FCM broadcast the widget consumed pre-OC-448. Inherently
 * site-filtered: the subscription is keyed by the currently selected site.
 */
export const useSiteCheckInEvents = ({ corporateId, siteId }, onEvent) =>
  useLatestCheckInDoc({ corporateId, subcollection: 'sites', docId: siteId }, onEvent)
